import { clsx } from "clsx";
import { FC, PropsWithChildren } from "react";
import { Link } from "react-router-dom";
import { route } from "../shim/jsbridge";

import RenderWhen from "./RenderWhen";
import ThreatStatusLabel from "./ThreatStatusLabel";

export type SampleRecord = {
  filename?: string;
  sha1?: string;
  source_id?: number;
  threat_status?: number | string;
  classification?: string;
  dropped_source_id?: number;
};

export type SampleDetailsLinkProps = {
  href?: string;
  sampleRecord: SampleRecord;
  showThreatColor?: boolean;
  showTitle?: boolean;
  reloadDocument?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  isUnavailable?: boolean;
};

const generateLink = (url: string, sampleRecord: SampleRecord) => {
  if (url) return url;
  const { sha1: hash_value, source_id, dropped_source_id } = sampleRecord || {};
  const routeName = source_id
    ? "samples_details_main"
    : "redirect_samples_details";

  if (dropped_source_id) {
    return route(routeName, { hash_value, source_id }, { dropped_source_id });
  }

  return route(routeName, { hash_value, source_id });
};

type SampleDetailsStatusLabelProps = Pick<
  SampleDetailsLinkProps,
  "sampleRecord" | "showThreatColor"
>;

const SampleDetailsStatusLabel: FC<SampleDetailsStatusLabelProps> = ({
  sampleRecord,
  showThreatColor = true
}) => (
  <ThreatStatusLabel
    showOrb={false}
    showColor={showThreatColor}
    threatStatus={sampleRecord.threat_status || sampleRecord.classification}
    labelText={sampleRecord.filename}
  />
);

const ValidLink: FC<SampleDetailsLinkProps> = props => {
  const {
    sampleRecord,
    showTitle = false,
    href = "",
    reloadDocument = false,
    disabled = false,
    onClick
  } = props;
  const link = generateLink(href, sampleRecord);

  return (
    <Link
      title={showTitle ? `See details for ${sampleRecord.filename}` : ""}
      to={link}
      onClick={e => {
        onClick?.();
        e.stopPropagation();
      }}
      className={clsx(
        "overflow-ellipsis min-content-width",
        disabled && "u-no-pointer-event"
      )}
      reloadDocument={reloadDocument}
      data-testid={`sample_details_link_${sampleRecord.filename}`}
    >
      <SampleDetailsStatusLabel {...props} />
    </Link>
  );
};

const InvalidLink: FC<SampleDetailsLinkProps> = props => {
  const { sampleRecord, onClick } = props;

  return (
    <span
      onClick={onClick}
      className={"overflow-ellipsis min-content-width u-no-pointer-event"}
      data-testid={`sample_details_link_${sampleRecord.filename}`}
    >
      <SampleDetailsStatusLabel {...props} />
    </span>
  );
};

const SampleDetailsLink: FC<
  PropsWithChildren<SampleDetailsLinkProps>
> = props => {
  const { sampleRecord, href = "", isUnavailable = false } = props;
  const linkIsValid =
    !isUnavailable && generateLink(href, sampleRecord) !== "/redirect//";

  return (
    <RenderWhen condition={linkIsValid} fallback={<InvalidLink {...props} />}>
      <ValidLink {...props} />
    </RenderWhen>
  );
};

export default SampleDetailsLink;
